import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type CompanyDocument,
  type DocumentFile,
  type DocumentPage,
  type DocumentSection,
  type FeedPage,
  type FetchDocumentsParams,
  type GuidePaymentError,
  type GuideStatusFilter,
  fetchDocumentFile,
  fetchDocuments,
  fetchFeedPage,
  fetchGuides,
  fetchSections,
  guidePaymentErrorOf,
  payGuide,
  unpayGuide,
} from '~/api/documents';
import { useModuleLevel } from '~/api/hooks/useCompany';
import { extractCursorFromUrl } from '~/api/invoices';
import { queryKeys } from '~/api/queryKeys';
import { ACCESS_LEVEL, atLeast, companyLevel } from '~/session/access';
import { useAuthStore } from '~/stores/auth';

/** O cursor de uma página do feed ou da lista; `null` é a primeira. */
type PageCursor = string | null;

export type FeedPages = InfiniteData<FeedPage, PageCursor>;
/** As páginas das guias de uma situação, na ordem em que chegaram. */
export type GuidePages = InfiniteData<DocumentPage, PageCursor>;

export const GUIDE_PAYMENT_ACTION = {
  pay: 'pay',
  unpay: 'unpay',
} as const;

export type GuidePaymentAction = (typeof GUIDE_PAYMENT_ACTION)[keyof typeof GUIDE_PAYMENT_ACTION];

/** O que a tela pede: marcar como paga ou desfazer, na guia de um documento. */
export interface GuidePaymentChange {
  documentId: string;
  action: GuidePaymentAction;
}

const GUIDE_PAYMENT_REQUEST: Record<
  GuidePaymentAction,
  (id: string, clientId: number) => Promise<CompanyDocument>
> = {
  pay: payGuide,
  unpay: unpayGuide,
};

/** O arquivo aberto no visualizador não muda com o pagamento: baixá-lo de novo trocaria a prévia. */
const FILE_KEY_SEGMENT = 'file';

/**
 * As consultas só saem com nível de consulta em Documentos na empresa em operação: abaixo disso
 * o backend responderia 403 e, com o `throwOnError` global, a tela cairia no error boundary.
 * Sem empresa não há consulta nenhuma, porque toda rota exige `?client=`.
 */
function useCanReadDocuments(): boolean {
  return atLeast(useModuleLevel('documents'), ACCESS_LEVEL.read);
}

/** "Paguei" e "Desfazer" pedem `documents` `FULL` na empresa da guia; o servidor confere de novo. */
export function useCanMarkGuides(clientId: number): boolean {
  const company = useAuthStore((state) => state.companies.find(({ id }) => id === clientId));
  return atLeast(companyLevel(company, 'documents'), ACCESS_LEVEL.full);
}

function nextPageCursor(lastPage: { next: string | null }): PageCursor {
  return extractCursorFromUrl(lastPage.next);
}

/** O feed por cursor, do post mais novo ao mais antigo; o `next` do DRF dá a página seguinte. */
export function useFeedInfiniteQuery(
  clientId: number | undefined,
): UseInfiniteQueryResult<FeedPages, Error> {
  const canRead = useCanReadDocuments();
  return useInfiniteQuery({
    queryKey: queryKeys.documents.feed(clientId ?? null),
    queryFn:
      clientId === undefined
        ? skipToken
        : ({ pageParam }: { pageParam: PageCursor }) => fetchFeedPage(clientId, pageParam),
    enabled: canRead,
    initialPageParam: null,
    getNextPageParam: nextPageCursor,
  });
}

/**
 * As guias atuais da empresa numa situação (a tela "Impostos"), pela lista de documentos com
 * `guide_status`, por cursor. A ordem por vencimento é da tela.
 */
export function useGuidesInfiniteQuery(
  clientId: number | undefined,
  status: GuideStatusFilter,
): UseInfiniteQueryResult<GuidePages, Error> {
  const canRead = useCanReadDocuments();
  return useInfiniteQuery({
    queryKey: queryKeys.documents.guides(clientId ?? null, status),
    queryFn:
      clientId === undefined
        ? skipToken
        : ({ pageParam }: { pageParam: PageCursor }) => fetchGuides(clientId, status, pageParam),
    enabled: canRead,
    initialPageParam: null,
    getNextPageParam: nextPageCursor,
  });
}

async function requestGuidePayment(
  clientId: number,
  { documentId, action }: GuidePaymentChange,
): Promise<CompanyDocument> {
  try {
    return await GUIDE_PAYMENT_REQUEST[action](documentId, clientId);
  } catch (error) {
    throw guidePaymentErrorOf(error);
  }
}

function isNotFileQuery(query: Query): boolean {
  return !query.queryKey.includes(FILE_KEY_SEGMENT);
}

/**
 * O "Paguei" e o desfazer. Ao terminar, dê certo ou não, invalida os documentos da empresa (as
 * guias de cada situação, o feed, as pastas): a guia muda de segmento, e uma recusa (paga por
 * outro, substituída) diz que a tela estava velha. O erro chega à tela como `{code, message}`
 * (`GuidePaymentError`), com a mensagem do servidor.
 */
export function useGuidePaymentMutation(
  clientId: number,
): UseMutationResult<CompanyDocument, GuidePaymentError, GuidePaymentChange> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: GuidePaymentChange) => requestGuidePayment(clientId, change),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.all,
        predicate: isNotFileQuery,
      }),
  });
}

/** As pastas: seções, tipos e períodos com documento atual, cada um com a contagem. */
export function useSectionsQuery(clientId: number | undefined): UseQueryResult<DocumentSection[]> {
  const canRead = useCanReadDocuments();
  return useQuery({
    queryKey: queryKeys.documents.sections(clientId ?? null),
    queryFn: clientId === undefined ? skipToken : () => fetchSections(clientId),
    enabled: canRead,
  });
}

/** A primeira página da lista filtrada (tipo, período, histórico); `limit` vai em `params`. */
export function useDocumentsQuery(
  clientId: number | undefined,
  params: FetchDocumentsParams = {},
): UseQueryResult<DocumentPage> {
  const canRead = useCanReadDocuments();
  return useQuery({
    queryKey: queryKeys.documents.list(clientId ?? null, params),
    queryFn: clientId === undefined ? skipToken : () => fetchDocuments(clientId, params),
    enabled: canRead,
  });
}

/**
 * O arquivo de um documento, baixado ao abrir o visualizador. Fica fresco enquanto ele está aberto
 * (a URL assinada vale sete dias) e sai do cache ao fechar (`gcTime` zero), e a chave nunca vai ao
 * cache persistido. O erro fica na tela, com "Tentar de novo", e não no error boundary.
 */
export function useDocumentFileQuery(
  clientId: number,
  documentId: string,
): UseQueryResult<DocumentFile> {
  const canRead = useCanReadDocuments();
  return useQuery({
    queryKey: queryKeys.documents.file(clientId, documentId),
    queryFn: () => fetchDocumentFile(documentId, clientId),
    enabled: canRead,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    throwOnError: false,
  });
}
